import type { ElectionNewsItem, NormalizedNewsItem } from '@/types/news'

const DEFAULT_NEWS_SOURCE_URL =
  'https://www.eltelegrafo.com.ec/medios/json/elecciones-seccionales-2026.json'
const DEFAULT_NEWS_REDIRECT_URL = 'https://www.eltelegrafo.com.ec/elecciones-seccionales-2027'
const LOCAL_NEWS_PROXY_URL = '/__local-news-feed'
let electionNewsRequest: Promise<NormalizedNewsItem[]> | null = null

function getNewsSourceUrl() {
  if (import.meta.env.DEV) return LOCAL_NEWS_PROXY_URL

  const configuredUrl = import.meta.env.VITE_NEWS_SOURCE_URL?.trim()
  if (!configuredUrl) return DEFAULT_NEWS_SOURCE_URL

  try {
    const parsedUrl = new URL(configuredUrl)
    return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:'
      ? parsedUrl.toString()
      : DEFAULT_NEWS_SOURCE_URL
  } catch {
    return DEFAULT_NEWS_SOURCE_URL
  }
}

export function getNewsRedirectUrl() {
  return import.meta.env.VITE_NEWS_REDIRECT_URL || DEFAULT_NEWS_REDIRECT_URL
}

function normalizeImageUrl(url: string | null): string | null {
  if (!url?.trim()) return null

  try {
    return new URL(url.trim(), 'https://www.eltelegrafo.com.ec').toString()
  } catch {
    return null
  }
}

function normalizeExternalUrl(url: string) {
  try {
    return new URL(url, 'https://www.eltelegrafo.com.ec').toString()
  } catch {
    return getNewsRedirectUrl()
  }
}

export function normalizeNewsItem(item: ElectionNewsItem): NormalizedNewsItem {
  return {
    id: String(item.id),
    publishedAt: item.fecha,
    title: item.titulo,
    excerpt: item.descripcion,
    imageUrl: normalizeImageUrl(item.foto),
    category: item.categoria,
    externalUrl: normalizeExternalUrl(item.url),
  }
}

async function fetchElectionNews(): Promise<NormalizedNewsItem[]> {
  const response = await fetch(getNewsSourceUrl())

  if (!response.ok) {
    throw new Error(`No se pudo cargar noticias: ${response.status}`)
  }

  const payload = (await response.json()) as ElectionNewsItem[]

  if (!Array.isArray(payload)) {
    throw new Error('La fuente de noticias no devolvio una lista valida.')
  }

  return payload.map(normalizeNewsItem)
}

export async function getElectionNews(): Promise<NormalizedNewsItem[]> {
  electionNewsRequest ??= fetchElectionNews().catch((error) => {
    electionNewsRequest = null
    throw error
  })

  return electionNewsRequest
}

export function sortNewsByNewest(news: NormalizedNewsItem[]) {
  function timestamp(value: string) {
    // The editorial feed uses Ecuador local time in YYYY-MM-DD HH:mm:ss.
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
      ? `${value.replace(' ', 'T')}-05:00`
      : value
    const time = Date.parse(normalized)
    return Number.isFinite(time) ? time : -Infinity
  }
  return [...news].sort((a, b) => {
    const first = timestamp(a.publishedAt)
    const second = timestamp(b.publishedAt)
    return first === second ? 0 : second - first
  })
}
